<template>
  <div class="propertyMapWrapper block lg:flex map-height">
    <SafeArea position="top" />
    <div
      v-if="isMinLarge"
      class="sidebar ios-fix"
    >
      <div>
        <div class="properties-sidebar-wrapper">
          <div class="properties-sidebar">
            <PropertyCompactList
              :properties="properties"
              :selected-property="selectedProperty"
              :selected-property-id="selectedPropertyId"
              :diplay-search-results="diplaySearchResults"
              style-classes="desktop-content-container"
              @open="openSelectedProperty"
            >
              <slot />
            </PropertyCompactList>
          </div>
        </div>
      </div>
    </div>
    <div class="relative w-full map-height">
      <AppMap
        v-if="isViewEntered"
        :key="appMapUpdateCounter"
        identifier="propertyMap"
        :map-settings="currentProject.mapConfig.map"
        :isMobile="isMobile" 
        style="width: 100%;"
        class="map-height"
        @map:loaded="handleMapLoaded"
        @map:zoom-overview="propertyMap?.zoomToOverview(currentProject.mapConfig.map)"
        @point:click="pointClicked"
      >
        <template #top>
          <PropertyMapFilter class="mt-2"/>
        </template>
        <CreatePropertyButton
          v-if="!isMobile"
          :organisation-prefix="organisationPrefix"
          :can-create-survey="canCreateSurvey && featureFlags?.createSurvey"
          @create-object="openCreateProperty"
          @create-survey="openCreateSurveyModal"
        />
      </AppMap>
    </div>
    <ion-modal
      v-if="isMobile"
      ref="modalDetailPage"
      class="modal-style show-as-foreground-modal"
      :is-open="isMobile && showModal && openedPropertyId"
      :backdrop-dismiss="false"
      :can-dismiss="true"
      :initial-breakpoint="initialBreakpointDetailPage"
      :breakpoints="breakpoints"
      :backdrop-breakpoint="breakpoints[4]"
      :keep-contents-mounted="true"
      @ion-breakpoint-did-change="onBreakpointChangeDetailModal($event)"
    >
      <div
        v-if="isPropertyDetailPageInitialized"
        class="modal-detail-container"
      >
        <PropertyDetail />
      </div>
    </ion-modal>

    <ion-modal
      v-if="isMobile"
      ref="modal"
      class="modal-style show-as-background-modal"
      :is-open="isMobile && showModal"
      :backdrop-dismiss="false"
      :can-dismiss="true"
      :initial-breakpoint="initialBreakpoint"
      :breakpoints="breakpoints"
      :backdrop-breakpoint="breakpoints[4]"
      :keep-contents-mounted="true"
      @ion-breakpoint-did-change="onBreakpointChange($event)"
      @did-present="onModalDidPresent()"
      @did-dismiss="onModalDidDismiss()"
    >
      <CreatePropertyButton
        v-if="isModalInitialized"
        :organisation-prefix="organisationPrefix"
        :can-create-survey="canCreateSurvey && featureFlags?.createSurvey"
        :is-mobile="true"
        @create-object="openCreateProperty"
        @create-survey="openCreateSurveyModal"
        @extended="setExtended($event)"
      />
      <PropertyCompactList
        v-if="isModalInitialized"
        :properties="properties"
        :selected-property="selectedProperty"
        :selected-property-id="selectedPropertyId"
        :diplay-search-results="diplaySearchResults"
        style-classes="modal-content-container"
        @open="openSelectedProperty"
      >
        <slot />
      </PropertyCompactList>
    </ion-modal>
  </div>
</template>

<script lang="ts">
import { IonButton, IonContent, IonHeader, IonIcon, IonLabel, IonList, IonModal, IonSpinner, modalController } from "@ionic/vue";
import { arrowBack } from "ionicons/icons";
import { ComputedRef, PropType, computed, defineComponent, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from 'vue-i18n';
import { useRouter } from "vue-router";
//Composables
import useScreenSize from "@/composables/useScreenSize";
import { useStore } from "@/composables/useTypedStore";
import { usePropertyMap } from "../../composables/usePropertyMap";
//Models
import Property from "@/models/immobilie.model";
import { getLocalMetaData } from "@/models/local-meta-data";
//Components
import AButton from "@/components/Base/AButton";
import AppMap from '@/components/v2/App/AppMap.vue';
import PropertyCard from "./PropertyCard.vue";
//Other
import _ from 'lodash';
import moment from "moment";
//Swiper
import PropertyDetail from "@/components/properties/PropertyDetail.vue";
import StatusFilter from '@/components/v2/Filters/Property/StatusFilter.vue';
import useUser from "@/composables/useUser";
import Immobilie from "@/models/immobilie.model";
import User from "@/models/user";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from '@capacitor/keyboard';
import { chevronForwardOutline, pinOutline, refreshOutline } from "ionicons/icons";
import { MapMouseEvent } from "maplibre-gl";
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import SafeArea from "../Base/SafeArea.vue";
import PersonFilter from "../v2/Filters/Property/PersonFilter.vue";
import PropertyMapFilter from "../v2/Filters/Property/PropertyMapFilter.vue";
import CreatePropertyButton from "./CreatePropertyButton.vue";
import PropertyCompactList from "./PropertyCompactList.vue";
import PropertyItem from "./PropertyItem.vue";
import PropertyList from "./PropertyList.vue";
import { openCreateSurveyModal } from "@/modals/modal-helper";

export default defineComponent({
  name: "PropertiesMap",
  components: {
    AButton,
    IonButton,
    IonIcon,
    AppMap,
    PropertyCard,
    Swiper,
    SwiperSlide,
    IonSpinner,
    IonModal,
    IonContent,
    PropertyList,
    IonList,
    PropertyItem,
    IonHeader,
    IonLabel,
    PropertyDetail,
    PersonFilter,
    StatusFilter,
    PropertyCompactList,
    CreatePropertyButton,
    PropertyMapFilter,
    SafeArea
  },
  props: {
    properties: {
      type: Object as PropType<Property[]>,
      required: true,
    },
    isViewEntered: {
      type: Boolean,
      required: true
    },
    // Necessary because properties.vue is initializing the map always independently of which view is selected
    parentIdentifier: {
      type: String,
      required: true
    },
    allowLoadMore: {
      type: Boolean,
      default: false
    }
  },
  emits: ["goBack", "loadMore"],
  setup(props, { emit }) {
    const { t } = useI18n({ useScope: 'global' });
    const store = useStore();
    // workaround because of the way properties.vue works
    const shouldMapInitialize = () => props.isViewEntered && props.parentIdentifier === 'propertiesMap';

    let propertyMap = shouldMapInitialize() ? usePropertyMap(props.properties) : null;    
    const selectedPropertyId = ref<number | null >(null);
    const hoveredProp = ref();
    const router = useRouter();
    const propertySlideList = ref([] as any[]);
    const currentProject = computed(() => store.state.user.currentUserProject);
    const propertySwiper = ref();
    const isMapLoaded = ref(false);
    const isPropertyDetailPageInitialized = ref(false);
    const isSwiperLoaded = ref(false);
    const showLoadingSpinner = ref(false);
    const footerHeight = document.documentElement.style.getPropertyValue('--bottomToolbarHeight') || "50px";
    const modal = ref<InstanceType<typeof IonModal>>();
    const modalDetailPage = ref<InstanceType<typeof IonModal>>();
    const isModalInitialized = ref(false);
    
    const breakpoints = [0.09, 0.3, 0.5, 0.7, 1.0];
    const initialBreakpoint = ref(breakpoints[0]);
    const initialBreakpointDetailPage = ref(breakpoints[4]);
    const isAddingExtended = ref(false);

    const { user, canCreateSurvey } = useUser();
    const featureFlags = computed(() => user.value?.organisation?.featureFlags);

    const selectedProperty = computed(() => {
      return props.properties.find((prop) => prop.id === selectedPropertyId.value);
    });

    const openedPropertyId = computed(() => {
      return store.getters["app/openedPropertyId"];
    });

    const appMapUpdateCounter = computed(() => {
      return store.state.app.appMapUpdateCounter;
    });

    const organisationPrefix: ComputedRef<string> = computed(() => {
      const currentUser: User | null = User.query().first();
      return currentUser ? currentUser.organisationPrefix : "";
    });

    const displayCountLabel = computed(() => {
      if (props.properties.length === 0) {
        return t("immobilie.searchResults.noResults", { type: t(`${organisationPrefix.value}toolbar.immobilien`) });
      } else if (props.properties.length === 1) {
        return t("immobilie.searchResults.resultsCount.one", { type: t(`${organisationPrefix.value}createSurvey.object`) });
      } else {
        return t("immobilie.searchResults.resultsCount.other", { count: props.properties.length, type: t(`${organisationPrefix.value}toolbar.immobilien`) });
      }
    });

    const diplaySearchResults = computed(() => {
      return `${t("immobilie.searchResults.title")} ${displayCountLabel.value ? `(${displayCountLabel.value})` : ''}`;
    });

    const showModal = computed(() => router.currentRoute.value.name === "propertiesMap");

    const debouncedFctn = _.debounce(() => {
      // changesLoading variable caused flickering of object card (show on/off/on/off,...)
      propertySlideList.value = JSON.parse(JSON.stringify(props.properties));
    }, 600);
    const isMobile = computed(() => {
      return store.getters["app/isMobile"];
    });

    const updatePropertyData = (properties: Property[]) => {
      if (propertyMap) {
          debouncedFctn();
          propertyMap?.updateProperties(properties);
          showLoadingSpinner.value = false;
      }
    };

    const watchViewEnter = watch(() => props.isViewEntered, (newVal) => {
      const isPropertyMap = shouldMapInitialize();
      if (newVal && isPropertyMap) {
        propertyMap = usePropertyMap(props.properties);
        updatePropertyData(props.properties);
        watchViewEnter();
      } else if (!isPropertyMap) {
        watchViewEnter();
      }
    });

    watch(() => props.properties, (newVal) => {
      // this code is called when reloading map, but not when entering map from übersicht (-> no slide list)
      updatePropertyData(newVal);
    }
    );

    // this is called when entering map and when reloading map,
    // so it is called twice when reloading (here and in watcher), but executed once due to debounce
    debouncedFctn();

    const { isMinLarge } = useScreenSize();

    const lastFetch = computed(() => {
      const date = moment(getLocalMetaData('lastBackendFetch'));
      return `${date.format('DD.MM.YYYY')} um ${date.format('HH:mm:ss')}`;
    });

    const cardClicked = (prop: any) => {
      hoveredProp.value = prop;
      propertyMap?.animateToProperty(prop);
    };
    // desktop view
    const openProperty = (prop: any) => {
      const routeData = router.resolve({ path: `/property/${prop.id}` });
      window.open(routeData.href, '_blank');
    };

    const onSlideChange = (e: any) => {
      const prop = propertySlideList.value[e.activeIndex];
      hoveredProp.value = prop;
      propertyMap?.animateToProperty(prop);
    };
    const onSwiperInit = (e: any) => {
      isSwiperLoaded.value = true;
      if (isMapLoaded.value) {
        flyToSlide(e.activeIndex);
      }
    };
    const flyToSlide = (goToIndex: any) => {
      if (hoveredProp.value && propertySwiper.value) {     // propertySwiper is undefined here
        // object is selected -> set slider to this object
        const propIndex = propertySlideList.value.findIndex((val) => { return val.id === hoveredProp.value.id; });
        propertySwiper.value?.slideTo(propIndex);

      } else { // no object selected -> fly to given index
        const prop = propertySlideList.value[goToIndex];
        hoveredProp.value = prop;
        propertyMap?.animateToProperty(prop);
      }
    };

    const handleMapLoaded = (map: any) => {
      propertyMap?.generateMap(map, currentProject.value.mapConfig.map);
      isMapLoaded.value = true;
      if (isSwiperLoaded.value) {
        flyToSlide(0);
      }
    };

    const loadMore = () => {
      if (props.allowLoadMore) {
        showLoadingSpinner.value = true;
        emit("loadMore");
      }
    };

    const setModalBreakpointByMinValue = async (minValue: number) => {
      const current = await modal.value?.$el.getCurrentBreakpoint();
      if (current && current < minValue) {
        modal.value?.$el.setCurrentBreakpoint(minValue);
      }
    };

    const checkModalBreakpointMaxValue = () => {
      // Go back to second highest breakpoint to keep extended adding-buttons visible
      if(modal.value && isAddingExtended.value) {
        if (initialBreakpoint.value > breakpoints[3]) {
          modal.value.$el.setCurrentBreakpoint(breakpoints[3]);
        }
      }
    };

    const scrollPropertyIntoView = (property: Immobilie | void) => {
      if (property) {
        const element = document.getElementById(`prop-item-${property.id}`);
        if (property && element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    };

    const pointClicked = async (e: MapMouseEvent) => {
      const propertyId = propertyMap?.getPropertyByPoint(e.point);
      if (propertyId) {
        selectedPropertyId.value = propertyId;
        propertyMap?.selectProperty(propertyId);
        setModalBreakpointByMinValue(breakpoints[2]);
        scrollPropertyIntoView(props.properties.find((prop) => prop.id === propertyId));
      } else {
        selectedPropertyId.value = null;
        propertyMap?.selectProperty(null);
      }
    };

    const openSelectedProperty = (property: Immobilie | null) => {
      if (property) {
        selectedPropertyId.value = property.id;
        propertyMap?.animateToProperty(property);
      }
    };


    const onBreakpointChange = (event: any) => {
      // remember when coming back from other tab
      initialBreakpoint.value = event.detail.breakpoint;
      checkModalBreakpointMaxValue();
    };

    const onBreakpointChangeDetailModal = (event: any) => {
      if (openedPropertyId.value) {
        // remember when coming back from other tab
        initialBreakpointDetailPage.value = event.detail.breakpoint;
      }
    };

    watch(openedPropertyId, (newVal) => {
      if (!newVal) {
        //reset to max value when modal is closed
        nextTick(() => {
          // using nextTick to ensure that the modal is already closed
          // there were some weird issues with the modal otherwise
          initialBreakpointDetailPage.value = breakpoints[4];
        });
      }
    });

    onBeforeUnmount(async () => {
      modal.value?.$el?.dismiss();
      modalDetailPage.value?.$el?.dismiss();
      if (Capacitor.isPluginAvailable("Keyboard")) {
        Keyboard?.removeAllListeners();
      }
      store.dispatch("app/setOpenedPropertyId", null);
    });

    if (Capacitor.isPluginAvailable("Keyboard")) {
      Keyboard?.addListener('keyboardWillShow', () => {
        document.documentElement.style.setProperty('--bottomToolbarHeight', "0px");
        setModalBreakpointByMinValue(breakpoints[1]);
      });

      Keyboard?.addListener('keyboardWillHide', () => {
        document.documentElement.style.setProperty('--bottomToolbarHeight', footerHeight);
      });
    }

    const unsubPropertyIdWatcher = watch(openedPropertyId, (newVal) => {
      if (newVal) {
        isPropertyDetailPageInitialized.value = true;
        unsubPropertyIdWatcher();
      }
    }, { immediate: true });

    const openCreateProperty = () => {
      router.push({ name: 'createProperty' });
    };

    const setExtended = (extended: boolean) => {
      isAddingExtended.value = extended;
      checkModalBreakpointMaxValue();
    };  

    const onModalDidPresent = () => {
      isModalInitialized.value = true;
    };

    const onModalDidDismiss = () => {
      isModalInitialized.value = false;
    };

    return {
      currentProject,
      t,
      isMinLarge,
      lastFetch,
      cardClicked,
      hoveredProp,
      arrowBack,
      openProperty,
      propertySlideList,
      swiperModules: [Navigation],
      handleMapLoaded,
      onSlideChange,
      onSwiperInit,
      propertyMap,
      showLoadingSpinner,
      loadMore,
      modal,
      isMobile,
      pinOutline,
      refreshOutline,
      pointClicked,
      selectedPropertyId,
      selectedProperty,
      diplaySearchResults,
      openedPropertyId,
      openSelectedProperty,
      chevronForwardOutline,
      featureFlags,
      initialBreakpoint,
      breakpoints,
      modalDetailPage,
      initialBreakpointDetailPage,
      onBreakpointChange,
      onBreakpointChangeDetailModal,
      showModal,
      displayCountLabel,
      scrollPropertyIntoView,
      openCreateProperty,
      isPropertyDetailPageInitialized,
      isModalInitialized,
      onModalDidPresent,
      onModalDidDismiss,
      appMapUpdateCounter,
      organisationPrefix,
      canCreateSurvey,
      setExtended,
      openCreateSurveyModal
    };
  }
});
</script>

<style lang="scss">
.propertyMapWrapper {

  --properties-sidebar-width: 500px;

  .swiperWrapper {
    position: absolute;
    bottom: 40px;
    left: 0;
    right: 0;
    width: 100%;
    overflow: hidden;

    .swiper {
      .swiper-wrapper {
        margin: 0px auto;

        .swiper-slide {
          ion-card {
            margin: 0 auto !important;
          }
        }
      }

      .swiper-button-next,
      .swiper-button-prev {
        color: var(--primary);
      }
    }
  }

  .sidebar {
    border-right: 1px solid #9f9f9f;
  }

  .properties-sidebar-wrapper {
    width: var(--properties-sidebar-width, 500px);
    background: #f3f5f9;
    overflow: auto;
    padding-right: 24px;
    padding-left: 24px;
    height: calc(100vh - var(--navigation-height, 60px) - var(--ion-safe-area-top, 0px) - var(--ion-safe-area-bottom, 0px));
    overflow-y: scroll;
  }

  .properties-sidebar {
    padding-bottom: 24px;
  }

  h1 {
    margin-top: 8px;
    margin-bottom: 8px;
  }

  .hovered {
    border: 1px solid black;
  }

  @media (max-width: 400px) {
    .property-card {
      width: 300px;
    }
  }

  .ios-fix {
    padding-top: unquote('constant(safe-area-inset-top))') !important;
    padding-top: env(safe-area-inset-top) !important;
  }

  @media (max-width: 1024px) {
    #propertyMap {
      width: 100vw !important;
    }
  }

  .tinycard {
    max-width: 18rem;
  }

}

ion-modal.modal-style {
  --safe-area: calc(env(safe-area-inset-bottom) + env(safe-area-inset-top));
  --height: calc(95vh - var(--safe-area, 0px) - var(--bottomToolbarHeight) - var(--create-button-height, 5rem)) !important;
  bottom: calc(var(--bottomToolbarHeight) + env(safe-area-inset-bottom, 0));
  --overflow: visible;

  &.show-as-foreground-modal {
    z-index: 16000 !important;
  }

  &.show-as-background-modal {
    z-index: 15000 !important;
  }
}

.modal-detail-container {
  height: 70vh;
  overflow-y: scroll;
}
</style>

<style
  scoped
  lang="scss"
>
:deep(.maplibregl-ctrl-attrib) {
  margin-right: 7rem !important;
}

.map-height {
  height: calc(100vh - var(--navigation-height, 60px) - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px));
}
</style>
